// =============================================================================
// Solicitações de orçamento — onde o Financeiro trata os pedidos dos gestores
//
// Validado em 23/09. O gestor pede pelo Painel do Gestor (aumento de uma linha
// num mês, ou remanejamento de onde sobra para onde falta); o pedido cai aqui,
// e o orçamento só muda quando o Financeiro aprova. Recusar exige parecer:
// é ele que explica ao gestor o motivo.
//
// Quem decide o que pode é a RPC (`rpc_decidir_solicitacao`, do Enzo): só
// financeiro e admin, só pedido em aberto, e no remanejamento move no máximo o
// saldo livre da origem no momento da aprovação. A tela só não oferece o que a
// RPC recusaria.
//
// Desde a 072, os pedidos de ferramenta (nova, cancelar, mudar plano) entram
// na mesma lista, com o tipo "Ferramenta". Além de aprovar e recusar, o
// Financeiro registra o encaminhamento a outro aprovador, e depois a decisão,
// dizendo quem aprovou.
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buscarPedidosFerramenta, buscarSolicitacoes, decidirPedidoFerramenta, decidirSolicitacao, encaminharPedidoFerramenta, FalhaApi,
  type PedidoFerramenta, type Solicitacao,
} from "../lib/api.ts";
import { dolar } from "../lib/ferramentas.ts";
import { CICLOS } from "../lib/ferramentaCiclo.ts";
import { ROTULO_TIPO_PEDIDO } from "./FerramentasArea.tsx";
import { competenciaCurta, moeda } from "../lib/formato.ts";
import { BarraFiltros, Campo, Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { CampoDinheiro } from "../componentes/CampoDinheiro.tsx";

type Filtro = "aberta" | "aprovada" | "rejeitada" | "todas";
const SITUACAO: Record<Solicitacao["estado"], { rotulo: string; tom: "atencao" | "ok" | "risco" }> = {
  aberta: { rotulo: "Em aberto", tom: "atencao" },
  aprovada: { rotulo: "Aprovada", tom: "ok" },
  rejeitada: { rotulo: "Recusada", tom: "risco" },
};
/** Carimbo do banco (UTC) → "23/09 14:02" em São Paulo (CLAUDE.md §12). */
function dataHora(ts: string | null | undefined): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
  });
}
const MODO: Record<string, string> = { linhas: "Entre linhas", meses: "Entre meses", ano: "No ano" };

function periodoDe(comps: string[] | null | undefined, fallback: string): string {
  const c = [...(comps?.length ? comps : [fallback])].sort();
  return c.length === 1 ? competenciaCurta(c[0]!) : `${competenciaCurta(c[0]!)} a ${competenciaCurta(c[c.length - 1]!)}`;
}

/** Diferença com sinal. Um pedido de aumento anterior à 047 pode ser MENOR que
 *  o atual, e o "+" fixo o mostrava como "+-R$ 10.000,00". */
function variacao(dif: bigint): string {
  return `${dif >= 0n ? "+" : ""}${moeda(dif.toString())}`;
}

function resumo(s: Solicitacao): string {
  if (s.tipo === "aumento") {
    const dif = BigInt(s.valor_solicitado_centavos) - BigInt(s.valor_atual_centavos ?? "0");
    return `${s.categoria_nome} · ${competenciaCurta(s.competencia)} · ${variacao(dif)}`;
  }
  const para = s.destino ? s.destino.categoria_nome : "—";
  return `${s.categoria_nome} → ${para} · ${moeda(s.valor_solicitado_centavos)}`;
}

/** O pedido de ferramenta na situação do filtro: encaminhado ainda está em aberto para o Financeiro. */
const ESTADO_PEDIDO: Record<PedidoFerramenta["estado"], Solicitacao["estado"]> = {
  aberto: "aberta", encaminhado: "aberta", aprovado: "aprovada", recusado: "rejeitada",
};

/** Uma linha da lista: pedido de orçamento ou de ferramenta (072). */
type Item =
  | { kind: "orcamento"; id: string; estado: Solicitacao["estado"]; centro: string; criado: string; s: Solicitacao }
  | { kind: "ferramenta"; id: string; estado: Solicitacao["estado"]; centro: string; criado: string; p: PedidoFerramenta };

/** `inicial`: a solicitação que o sino mandou abrir. */
export function Solicitacoes({ inicial }: { inicial?: string | null }) {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>("aberta");
  const [tipo, setTipo] = useState<"todos" | "aumento" | "remanejamento" | "ferramenta">("todos");
  const [cc, setCc] = useState("todos");
  const [selecionada, setSelecionada] = useState<string | null>(inicial ?? null);
  useEffect(() => { if (inicial) setSelecionada(inicial); }, [inicial]);

  const q = useQuery({ queryKey: ["solicitacoes"], queryFn: buscarSolicitacoes });
  const qp = useQuery({ queryKey: ["ferramentas-pedidos"], queryFn: buscarPedidosFerramenta });
  const todas = useMemo<Item[]>(() => [
    ...(q.data?.solicitacoes ?? []).map((s): Item => ({ kind: "orcamento", id: s.id, estado: s.estado, centro: s.centro_custo_id, criado: s.criado_em, s })),
    ...(qp.data?.pedidos ?? []).map((p): Item => ({ kind: "ferramenta", id: p.id, estado: ESTADO_PEDIDO[p.estado], centro: p.centro_custo_id, criado: p.criado_em, p })),
  ].sort((a, b) => Number(b.estado === "aberta") - Number(a.estado === "aberta") || b.criado.localeCompare(a.criado)), [q.data, qp.data]);

  const ccs = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of todas) {
      const [cod, nome] = i.kind === "orcamento" ? [i.s.centro_custo_codigo, i.s.centro_custo_nome] : [i.p.centro_custo_codigo, i.p.centro_custo_nome];
      m.set(i.centro, `${cod} · ${nome}`);
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [todas]);

  const lista = todas.filter((i) =>
    (filtro === "todas" || i.estado === filtro)
    && (tipo === "todos" || (i.kind === "ferramenta" ? tipo === "ferramenta" : i.s.tipo === tipo))
    && (cc === "todos" || i.centro === cc));
  // A escolhida continua no detalhe mesmo depois de sair do filtro — aprovar
  // um pedido "em aberto" o tira da lista, e o aviso da decisão sumiria junto.
  const atual = todas.find((i) => i.id === selecionada) ?? lista[0] ?? null;
  const contar = (f: Filtro) => todas.filter((i) => f === "todas" || i.estado === f).length;
  const aoDecidir = () => qc.invalidateQueries({
    predicate: (x) => ["solicitacoes", "ferramentas-pedidos", "ferramentas", "ferramentas-area", "contadores", "avisos", "planilha",
      "orcado-realizado", "painel-gestor", "visao-financeiro"].includes(String(x.queryKey[0])),
  });

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Operacional</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Solicitações
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          Pedidos dos gestores: aumento e remanejamento do orçamento, e ferramenta nova, cancelamento ou mudança de plano.
          Nada muda antes de o Financeiro aprovar.
        </p>
      </header>

      <BarraFiltros className="mb-5">
        <Campo rotulo="Situação">
          <div role="group" className="flex flex-wrap overflow-hidden rounded-lg border border-linha">
            {([["aberta", "Em aberto"], ["aprovada", "Aprovadas"], ["rejeitada", "Recusadas"], ["todas", "Todas"]] as const).map(([v, r]) => (
              <button key={v} type="button" aria-pressed={filtro === v} onClick={() => setFiltro(v)}
                className={`px-3 py-2 text-[12.5px] font-semibold ${filtro === v ? "bg-azul text-white" : "bg-superficie text-tinta-media hover:bg-superficie-2"}`}>
                {r} <span className="num opacity-75">{contar(v)}</span>
              </button>
            ))}
          </div>
        </Campo>
        <Campo rotulo="Tipo">
          <select value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}
            className="min-w-[160px] rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px]">
            <option value="todos">Todos</option>
            <option value="aumento">Aumento</option>
            <option value="remanejamento">Remanejamento</option>
            <option value="ferramenta">Ferramenta</option>
          </select>
        </Campo>
        <Campo rotulo="Centro de custo">
          <select value={cc} onChange={(e) => setCc(e.target.value)}
            className="min-w-[240px] rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px]">
            <option value="todos">Todos os centros de custo</option>
            {ccs.map(([id, rotulo]) => <option key={id} value={id}>{rotulo}</option>)}
          </select>
        </Campo>
      </BarraFiltros>

      {(q.isPending || qp.isPending) && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar as solicitações" dica={(q.error as FalhaApi).message} />}
      {qp.isError && <Vazio titulo="Não foi possível carregar os pedidos de ferramenta" dica={(qp.error as FalhaApi).message} />}

      {q.data && qp.data && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(280px,0.8fr)_1.4fr]">
          <div className="grid gap-2">
            {lista.length === 0 && <Vazio titulo="Nenhuma solicitação nesta situação" />}
            {lista.map((i) => {
              const sit = SITUACAO[i.estado];
              const ativa = atual?.id === i.id;
              const encaminhado = i.kind === "ferramenta" && i.p.estado === "encaminhado";
              return (
                <button key={i.id} type="button" onClick={() => setSelecionada(i.id)} aria-current={ativa}
                  className={`grid gap-1 rounded-xl border bg-superficie px-4 py-3 text-left ${ativa ? "border-laranja ring-1 ring-laranja" : "border-linha hover:border-linha-forte"}`}>
                  <span className="flex items-center justify-between gap-2 text-[13px] font-semibold">
                    {i.kind === "ferramenta" ? `Ferramenta · ${ROTULO_TIPO_PEDIDO[i.p.tipo]}` : i.s.tipo === "aumento" ? "Aumento" : "Remanejamento"}
                    <Pilula tom={sit.tom}>{encaminhado ? "Encaminhado" : sit.rotulo}</Pilula>
                  </span>
                  <span className="num text-[12.5px]">{i.kind === "ferramenta" ? resumoPedido(i.p) : resumo(i.s)}</span>
                  <span className="text-[11.5px] text-tinta-fraca">
                    {(i.kind === "ferramenta" ? i.p.solicitante_nome : i.s.solicitante_nome) ?? "—"} ·{" "}
                    {i.kind === "ferramenta" ? i.p.centro_custo_codigo : i.s.centro_custo_codigo} · {dataHora(i.criado)}
                  </span>
                </button>
              );
            })}
          </div>

          {atual?.kind === "orcamento" && <Detalhe key={atual.id} s={atual.s} onDecidido={aoDecidir} />}
          {atual?.kind === "ferramenta" && <DetalhePedido key={atual.id} p={atual.p} onDecidido={aoDecidir} />}
        </div>
      )}
    </div>
  );
}

function Detalhe({ s, onDecidido }: { s: Solicitacao; onDecidido: () => void }) {
  const [parecer, setParecer] = useState("");
  const [outroValor, setOutroValor] = useState(false);
  const [valor, setValor] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const sit = SITUACAO[s.estado];

  const decidir = useMutation({
    mutationFn: (decisao: "aprovar" | "recusar") =>
      decidirSolicitacao(s.id, {
        decisao,
        parecer: parecer.trim(),
        valor_aprovado_centavos: decisao === "aprovar" && outroValor ? valor : null,
      }),
    onSuccess: (r, decisao) => {
      setAviso(r.aviso ?? (decisao === "aprovar"
        ? "Aprovada. O orçamento foi alterado e a mudança está no Histórico."
        : "Recusada. O gestor vê o parecer no painel dele."));
      onDecidido();
    },
  });

  const atualC = BigInt(s.valor_atual_centavos ?? "0");
  const pedidoC = BigInt(s.valor_solicitado_centavos);

  return (
    <Painel>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-[16px] font-bold">
            {s.tipo === "aumento" ? "Aumento" : `Remanejamento · ${MODO[s.modo ?? ""] ?? ""}`}
          </h2>
          <p className="mt-0.5 text-[12px] text-tinta-fraca">
            {s.solicitante_nome ?? "—"} · enviado em {dataHora(s.criado_em)}
          </p>
        </div>
        <Pilula tom={sit.tom}>{sit.rotulo}</Pilula>
      </div>

      {s.tipo === "aumento" ? (
        <dl className="mt-4 grid gap-2 rounded-xl border border-linha p-4 text-[13px]">
          <div className="text-[10.5px] font-bold tracking-wide text-ok-tinta uppercase">
            {s.centro_custo_codigo} · {s.centro_custo_nome} · {s.categoria_nome} · {competenciaCurta(s.competencia)}
          </div>
          <Par rotulo="Orçado quando pediu" valor={moeda(atualC.toString())} />
          <Par rotulo="Solicitado" valor={`${moeda(pedidoC.toString())} (${variacao(pedidoC - atualC)})`} />
          {s.valor_aprovado_centavos && <Par rotulo="Aprovado" valor={moeda(s.valor_aprovado_centavos)} />}
        </dl>
      ) : (
        <div className="mt-4 grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <div className="rounded-xl border border-linha p-4 text-[13px]">
            <div className="text-[10.5px] font-bold tracking-wide text-risco-tinta uppercase">De</div>
            <p className="mt-1 font-semibold">{s.centro_custo_codigo} · {s.categoria_nome}</p>
            <p className="text-tinta-fraca">{periodoDe(s.competencias_origem, s.competencia)}</p>
          </div>
          <div aria-hidden className="grid place-items-center text-[22px] font-bold text-laranja">→</div>
          <div className="rounded-xl border border-linha p-4 text-[13px]">
            <div className="text-[10.5px] font-bold tracking-wide text-ok-tinta uppercase">Para</div>
            <p className="mt-1 font-semibold">{s.destino ? `${s.destino.centro_custo_codigo} · ${s.destino.categoria_nome}` : "—"}</p>
            <p className="text-tinta-fraca">{s.destino ? periodoDe(s.destino.competencias, s.competencia) : ""}</p>
          </div>
          <p className="num text-[13px] sm:col-span-3">
            Valor pedido: <strong>{moeda(s.valor_solicitado_centavos)}</strong>
            {s.valor_aprovado_centavos && s.valor_aprovado_centavos !== s.valor_solicitado_centavos &&
              <> · movido: <strong>{moeda(s.valor_aprovado_centavos)}</strong> (o saldo livre da origem era menor)</>}
          </p>
        </div>
      )}

      <blockquote className="mt-4 border-l-[3px] border-linha-forte pl-3 text-[13px] leading-relaxed text-tinta-media">
        <span className="block text-[10.5px] font-bold tracking-wide text-tinta-fraca uppercase">Justificativa do gestor</span>
        {s.motivo || "—"}
      </blockquote>

      {aviso ? (
        <p role="status" className="mt-4 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta">{aviso}</p>
      ) : s.estado === "aberta" ? (
        <div className="mt-4 grid gap-3 rounded-xl border border-linha bg-[#fbfcfe] p-4">
          {s.tipo === "aumento" && (
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={outroValor} onChange={(e) => setOutroValor(e.target.checked)} className="accent-azul" />
              Aprovar outro valor
            </label>
          )}
          {s.tipo === "aumento" && outroValor && (
            <label className="block">
              <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">Valor aprovado para o mês</span>
              <CampoDinheiro valorCentavos={valor} onMudar={setValor} />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">Parecer do Financeiro</span>
            <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
              placeholder="Obrigatório para recusar. Vai para o gestor e fica no Histórico."
              className="mt-1 w-full rounded-lg border border-linha px-2.5 py-1.5 text-[13px]" />
          </label>
          {decidir.isError && (
            <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
              {(decidir.error as FalhaApi).message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" disabled={decidir.isPending || parecer.trim().length < 5}
              title={parecer.trim().length < 5 ? "Escreva o parecer para recusar" : undefined}
              onClick={() => decidir.mutate("recusar")}
              className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media disabled:cursor-not-allowed disabled:opacity-45">
              Recusar
            </button>
            <button type="button"
              disabled={decidir.isPending || (outroValor && (!valor || BigInt(valor) <= 0n))}
              onClick={() => decidir.mutate("aprovar")}
              className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
              {decidir.isPending ? "Gravando…" : outroValor ? "Aprovar outro valor" : "Aprovar"}
            </button>
          </div>
        </div>
      ) : (
        <p className={`mt-4 rounded-lg border px-3 py-2 text-[13px] ${s.estado === "aprovada" ? "border-ok-linha bg-ok-bg text-ok-tinta" : "border-linha bg-superficie-2 text-tinta-media"}`}>
          <strong>{sit.rotulo}</strong>
          {s.decidido_por_nome && <> por {s.decidido_por_nome}</>}
          {s.decidido_em && <> em {dataHora(s.decidido_em)}</>}
          {s.parecer && <><br />{s.parecer}</>}
        </p>
      )}
    </Painel>
  );
}

const valorPedido = (m: "BRL" | "USD" | null, v: string | null) => (v === null ? "—" : m === "USD" ? dolar(v) : moeda(v));
const rotuloCiclo = (c: string | null) => CICLOS.find((x) => x.valor === c)?.rotulo ?? "—";
const data = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

function resumoPedido(p: PedidoFerramenta): string {
  if (p.tipo === "cancelar") return `${p.ferramenta_nome}${p.a_partir_de ? ` · a partir de ${data(p.a_partir_de)}` : ""}`;
  return `${p.ferramenta_nome} · ${rotuloCiclo(p.ciclo)} · ${valorPedido(p.moeda, p.valor_ciclo_centavos)}`;
}

const MENSAGEM_APROVADO: Record<PedidoFerramenta["tipo"], string> = {
  nova: "Aprovado. A ferramenta entrou no cadastro como nova: complete a conta, o cartão e o resto em Ferramentas e assinaturas.",
  cancelar: "Aprovado. A ferramenta ficou como cancelada no cadastro; se a cobrança continuar vindo, o sistema avisa em Pendências.",
  alterar: "Aprovado. O cadastro passou a ter a periodicidade e o valor novos.",
};

/** O pedido de ferramenta (072): aprovar, recusar com parecer, ou registrar o encaminhamento. */
function DetalhePedido({ p, onDecidido }: { p: PedidoFerramenta; onDecidido: () => void }) {
  const [parecer, setParecer] = useState("");
  const [aprovadoPor, setAprovadoPor] = useState(p.encaminhado_a ?? "");
  const [encaminhando, setEncaminhando] = useState(false);
  const [para, setPara] = useState("");
  const [comentario, setComentario] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const aberto = p.estado === "aberto" || p.estado === "encaminhado";
  const sit = SITUACAO[ESTADO_PEDIDO[p.estado]];

  const decidir = useMutation({
    mutationFn: (decisao: "aprovar" | "recusar") =>
      decidirPedidoFerramenta(p.id, { decisao, parecer: parecer.trim(), aprovado_por: aprovadoPor.trim() || undefined }),
    onSuccess: (_r, decisao) => {
      setAviso(decisao === "recusar" ? "Recusado. O gestor vê o parecer na aba Ferramentas do painel dele." : MENSAGEM_APROVADO[p.tipo]);
      onDecidido();
    },
  });
  const encaminhar = useMutation({
    mutationFn: () => encaminharPedidoFerramenta(p.id, para.trim(), comentario.trim()),
    onSuccess: () => { setEncaminhando(false); setAprovadoPor(para.trim()); onDecidido(); },
  });
  const erro = (decidir.error ?? encaminhar.error) as FalhaApi | null;
  const rotuloCampo = "text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase";
  const campo = "mt-1 w-full rounded-lg border border-linha px-2.5 py-1.5 text-[13px]";

  return (
    <Painel>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-[16px] font-bold">Ferramenta · {ROTULO_TIPO_PEDIDO[p.tipo]}</h2>
          <p className="mt-0.5 text-[12px] text-tinta-fraca">{p.solicitante_nome ?? "—"} · enviado em {dataHora(p.criado_em)}</p>
        </div>
        <Pilula tom={sit.tom}>{p.estado === "encaminhado" ? "Encaminhado" : sit.rotulo}</Pilula>
      </div>

      <dl className="mt-4 grid gap-2 rounded-xl border border-linha p-4 text-[13px]">
        <div className="text-[10.5px] font-bold tracking-wide text-ok-tinta uppercase">
          {p.centro_custo_codigo} · {p.centro_custo_nome} · {p.ferramenta_nome}
        </div>
        {p.tipo === "alterar" && (
          <Par rotulo="Hoje" valor={`${rotuloCiclo(p.atual_ciclo)} · ${valorPedido(p.atual_moeda, p.atual_valor_ciclo_centavos)}`} />
        )}
        {p.tipo !== "cancelar" && (
          <Par rotulo={p.tipo === "alterar" ? "Pedido" : "Periodicidade e valor"}
            valor={`${rotuloCiclo(p.ciclo)} · ${valorPedido(p.moeda, p.valor_ciclo_centavos)}`} />
        )}
        {p.a_partir_de && <Par rotulo={p.tipo === "cancelar" ? "Cancelar a partir de" : "A partir de"} valor={data(p.a_partir_de)} />}
        {p.tipo === "nova" && <Par rotulo="Responsável" valor={p.responsavel ?? "—"} />}
        {p.tipo === "nova" && p.teste && <Par rotulo="Teste gratuito" valor="Começa em teste" />}
        {p.url && (
          <div className="flex justify-between gap-3">
            <dt className="text-tinta-media">Site</dt>
            <dd className="truncate"><a href={p.url} target="_blank" rel="noreferrer noopener" className="text-azul underline">{p.url}</a></dd>
          </div>
        )}
      </dl>

      <blockquote className="mt-4 border-l-[3px] border-linha-forte pl-3 text-[13px] leading-relaxed text-tinta-media">
        <span className="block text-[10.5px] font-bold tracking-wide text-tinta-fraca uppercase">
          {p.tipo === "nova" ? "Para que serve" : p.tipo === "cancelar" ? "Por que cancelar" : "O que muda"}
        </span>
        {p.motivo}
      </blockquote>

      {p.estado === "encaminhado" && !aviso && (
        <p className="mt-4 rounded-lg bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
          Encaminhado a <b>{p.encaminhado_a}</b>{p.encaminhado_em && <> em {dataHora(p.encaminhado_em)}</>}.
          {p.encaminhamento && <><br />{p.encaminhamento}</>}
          <br />Quando houver a resposta, registre aqui a decisão.
        </p>
      )}

      {aviso ? (
        <p role="status" className="mt-4 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta">{aviso}</p>
      ) : aberto ? (
        <div className="mt-4 grid gap-3 rounded-xl border border-linha bg-[#fbfcfe] p-4">
          {encaminhando ? (
            <>
              <label className="block">
                <span className={rotuloCampo}>Encaminhado a</span>
                <input value={para} onChange={(e) => setPara(e.target.value)} placeholder="Quem vai decidir (por exemplo, a diretoria)" className={campo} />
              </label>
              <label className="block">
                <span className={rotuloCampo}>Comentário</span>
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} className={campo} />
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEncaminhando(false)}
                  className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media">Voltar</button>
                <button type="button" disabled={encaminhar.isPending || !para.trim()} onClick={() => encaminhar.mutate()}
                  className="rounded-lg bg-azul px-4 py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-45">
                  {encaminhar.isPending ? "Gravando…" : "Registrar o encaminhamento"}
                </button>
              </div>
            </>
          ) : (
            <>
              {p.estado === "encaminhado" && (
                <label className="block">
                  <span className={rotuloCampo}>Quem aprovou</span>
                  <input value={aprovadoPor} onChange={(e) => setAprovadoPor(e.target.value)} className={campo} />
                </label>
              )}
              <label className="block">
                <span className={rotuloCampo}>Parecer do Financeiro</span>
                <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
                  placeholder="Obrigatório para recusar. Vai para o gestor e fica no Histórico." className={campo} />
              </label>
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => { setPara(p.encaminhado_a ?? ""); setEncaminhando(true); }}
                  className="mr-auto rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media">
                  {p.estado === "encaminhado" ? "Encaminhar a outra pessoa" : "Encaminhar a outro aprovador"}
                </button>
                <button type="button" disabled={decidir.isPending || parecer.trim().length < 5}
                  title={parecer.trim().length < 5 ? "Escreva o parecer para recusar" : undefined}
                  onClick={() => decidir.mutate("recusar")}
                  className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media disabled:cursor-not-allowed disabled:opacity-45">
                  Recusar
                </button>
                <button type="button" disabled={decidir.isPending} onClick={() => decidir.mutate("aprovar")}
                  className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
                  {decidir.isPending ? "Gravando…" : "Aprovar"}
                </button>
              </div>
            </>
          )}
          {erro && <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{erro.message}</p>}
        </div>
      ) : (
        <p className={`mt-4 rounded-lg border px-3 py-2 text-[13px] ${p.estado === "aprovado" ? "border-ok-linha bg-ok-bg text-ok-tinta" : "border-linha bg-superficie-2 text-tinta-media"}`}>
          <strong>{sit.rotulo}</strong>
          {p.aprovado_por && <> por {p.aprovado_por}</>}
          {p.decidido_por_nome && <> · registrado por {p.decidido_por_nome}</>}
          {p.decidido_em && <> em {dataHora(p.decidido_em)}</>}
          {p.parecer && <><br />{p.parecer}</>}
        </p>
      )}
    </Painel>
  );
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-tinta-media">{rotulo}</dt>
      <dd className="num font-semibold">{valor}</dd>
    </div>
  );
}
